/**
 * This script is used to refresh the image generation in the cart 
 * so that if a user adds a product to the cart but the photo is 
 * not generated, it will be generated and added to the product.
 */
class CartCrystalGenerate {
  BASE_URL = window.location.origin;

  constructor() {
    if (window.DCCOMLivePreview?.disableV2) return;
    const STORAGE_KEY = 'cart-crystal-generate';
    this.STORAGE = new Proxy(JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}"), {
      set(target, p, newValue, receiver) {
        const status = Reflect.set(target, p, newValue, receiver);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(target));
        return status;
      },
      get(target, p) {
        return Reflect.get(target, p);
      },
      deleteProperty(target, p) {
        const status = Reflect.deleteProperty(target, p);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(target));
        return status;
      }
    })
    this.synchronize();
  }

  /**
   * Synchronize images
   */
  synchronize() {
    this.items.then((items) => {
      items.forEach(async (item) => {
        const hashKey = await this.createItemHash(item);
        if (this.isV3Item(item)) {
          this.setLoadingState(item, false);
          if (this.STORAGE[hashKey]) delete this.STORAGE[hashKey];
          return;
        }
        if (this.needSync(item)) {
          this.setLoadingState(item, true);
          if (!(hashKey in this.STORAGE)) {
            const preview = await this.generatePreviewBatch(item);
            if (preview) {
              this.STORAGE[hashKey] = {
                preview: preview,
                date: new Date(),
              };
            }
          }
        } else {
          this.setLoadingState(item, false);
          if (this.STORAGE[hashKey]) {
            delete this.STORAGE[hashKey];
            console.log(`${hashKey} image generated successfully.`);
          }
        }
      })

      Object.entries(this.STORAGE).forEach(async ([hashKey, {date, preview}]) => {
        let matchedItem = null;
        for (const item of items) {
          const itemHashKey = await this.createItemHash(item);
          if (itemHashKey === hashKey) {
            matchedItem = item;
            break;
          }
        }
        if (matchedItem && this.isV3Item(matchedItem)) {
          delete this.STORAGE[hashKey];
          return;
        }
        if (!preview) {
          delete this.STORAGE[hashKey];
          return;
        }
        if (new Date(Date.now() - 30000) > date) {
          console.log(`${hashKey} timeout, restart!`)
          delete this.STORAGE[hashKey];
          return;
        }
        try {
          const image = await this.getPreviewImage(preview);
          if (image && (image.status === 'completed' || image.status === 'cached')) {
            if (matchedItem) {
              await this.save(matchedItem, {
                _preview_image_url: image.result_url,
                _preview_image_thumbnail_url: image.thumbnail_url || image.result_url,
              })
            }

            delete this.STORAGE[hashKey];
          }
        } catch (e) {
          console.error(e);
        }
      })
    }).finally(() => {
      setTimeout(() => {
        this.synchronize();
      }, 5000);
    })
  }

  setLoadingState(item, isLoading) {
    const lineKey = item && item.key ? item.key : '';
    if (!lineKey) return;

    const selector = `[data-line-key="${this.escapeSelector(lineKey)}"]`;
    const cartItem = document.querySelector(selector);
    if (!cartItem) return;

    cartItem.classList.toggle('cart-item--preview-loading', isLoading);

    const imageContainer = cartItem.querySelector('.cart-item__image-container');
    if (!imageContainer) return;

    let spinner = imageContainer.querySelector('.engrave-loading-spinner');
    if (isLoading) {
      if (!spinner) {
        spinner = document.createElement('span');
        spinner.className = 'engrave-loading-spinner';
        spinner.setAttribute('aria-hidden', 'true');
        imageContainer.append(spinner);
      }
    } else if (spinner) {
      spinner.remove();
    }
  }

  escapeSelector(value) {
    if (window.CSS && typeof window.CSS.escape === 'function') {
      return window.CSS.escape(value);
    }
    return String(value).replace(/[^\w-]/g, '\\$&');
  }

  async save(item, additionalAttributes = {}) {
    const updatedCart = await this.cartChange({
      id: item.key,
      properties: Object.assign(item.properties, additionalAttributes)
    });
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-crystal-generate', cartData: updatedCart });
    }
    return updatedCart;
  }

  /**
   * Change cart
   *
   * @param data
   * @returns {Promise<*>}
   */
  cartChange(data) {
    return this.cart_call('change', data);
  }

  /**
   * Cart request
   *
   * @param {string} method
   * @param {object} data
   * @returns {Promise<any>}
   */
  cart_call(method = 'update', data) {
    return fetch(this.BASE_URL.concat('/cart/', method, '.js'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    })
      .then(response => {
        return response.json();
      })
      .catch((error) => {
        return Promise.reject(error);
      });
  }

  /**
   * Get preview image from api
   *
   * @param preview
   * @returns {Promise<any>}
   */
  async getPreviewImage(preview) {
    if (!preview || !preview.batch_id || !preview.item_id) {
      return null;
    }
    return fetch(this.BASE_URL.concat('/apps/crystal-settings/api/v2/previews/', preview.batch_id, '?v', Math.random().toString()), {
      cache: "no-store"
    })
      .then(response => response.json())
      .then((batch) => {
        const items = Array.isArray(batch.items) ? batch.items : [];
        return items.find((item) => item.item_id === preview.item_id);
      });
  }

  /**
   *
   * @param {*} item
   */
  generatePreviewBatch(item) {
    return new Promise((resolve, reject) => {
      if (!item?.properties?.Photo) {
        reject(new Error('Photo not provided'));
        return;
      }
      fetch(this.BASE_URL.concat('/apps/crystal-settings/api/v2/templates/', this.getItemSKU(item)))
        .then((response) => response.json())
        .then(({ images }) => {
          if (!Array.isArray(images) || images.length === 0) {
            reject(new Error(`Templates not found`));
            return;
          }
          const templateId = this.pickTemplateId(images);
          const engraveText = this.data_get(item, 'properties.Engrave Text', '') ||
            this.data_get(item, 'properties.Engrave text', '');
          fetch(this.BASE_URL.concat('/apps/crystal-settings/api/v2/previews'), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              source_image_url: this.optimizePhotoUrl(item.properties['Photo']),
              source_font: item.properties['Font'] || 'Georgia',
              source_text: engraveText,
              sku: this.getItemSKU(item),
              template_ids: [templateId]
            })
          })
            .then((response) => response.json())
            .then((batch) => {
              const items = Array.isArray(batch.items) ? batch.items : [];
              if (batch.batch_id && items.length > 0 && items[0].item_id) {
                resolve({ batch_id: batch.batch_id, item_id: items[0].item_id });
              } else {
                reject(new Error(`Preview batch not found`));
              }
            })
            .catch((error) => {
              reject(error);
            });
        }).catch((error) => {
          reject(error);
      })
    })
  }

  pickTemplateId(images) {
    const noBase = images.find((image) => !image.light_base_sku);
    return (noBase && noBase.id) ? noBase.id : images[0].id;
  }

  /**
   * Optimize image link
   *
   * @param url
   * @returns {string}
   */
  optimizePhotoUrl(url) {
    if (!url) {
      throw new Error('Photo URL not provided');
    }

    let image_url_object;
    try {
      image_url_object = new URL(url, window.location.origin);
    } catch (error) {
      throw new Error('Invalid photo URL');
    }

    if (!['http:', 'https:'].includes(image_url_object.protocol)) {
      throw new Error('Invalid photo URL');
    }

    if (image_url_object.pathname.startsWith('/products/')) {
      throw new Error('Invalid photo URL');
    }

    const archived = window.NativePhotoCompat?.legacyUrl(image_url_object.href);
    if (archived) return archived;

    if (/\.(avif|gif|heic|heif|jpe?g|png|webp)$/i.test(image_url_object.pathname)) {
      return image_url_object.href;
    }

    throw new Error('Invalid photo URL');
  }

  /**
   * Get item sku
   *
   * @param item
   * @returns {string}
   */
  getItemSKU(item) {
    if (item.sku.length === 15 || item.sku.length === 16) {
      return item.sku;
    }
    let sku = item.sku
      .replace('-CUSTOM', '')
      .replace('-CUSTM', '');
    if (sku.includes("-")) {
      return sku.concat("-CUSTM").slice(0, 16)
    }
    return sku.concat("-A-CUSTM").slice(0, 16);
  }


  /**
   * Verify if item need sync
   *
   * @param item
   * @returns {boolean}
   */
  needSync(item) {
    return !this.isV3Item(item) && this.data_get(item, 'properties.Photo') && (
      !this.data_get(item, 'properties._preview_image_url') ||
      !this.data_get(item, 'properties._preview_image_thumbnail_url')
    );
  }

  isV3Item(item) {
    return Boolean(this.data_get(item, 'properties._live_preview_v3_session_id'));
  }

  /**
   * Create item hash key
   *
   * @param item
   * @returns {Promise<string>}
   */
  async createItemHash(item) {
    const engraveText = this.data_get(item, 'properties.Engrave Text', '') ||
      this.data_get(item, 'properties.Engrave text', '');
    return await this.digestMessage([
      engraveText,
      this.data_get(item, 'properties.Font', ''),
      this.data_get(item, 'properties._Group Identify', ''),
      this.data_get(item, 'properties.Photo', ''),
    ].join(', '))
  }

  /**
   * Get an item from an object or array using dot notation.
   *
   * @param {object|array} data
   * @param {string} path
   * @param {*} [defaultValue=undefined]
   * @returns {*}
   */
  data_get(data, path, defaultValue = undefined) {
    if (data === null || typeof data === 'undefined') {
      return defaultValue;
    }

    const pathParts = path.split('.');
    let current = data;

    for (let i = 0; i < pathParts.length; i++) {
      const part = pathParts[i];

      if (typeof current === 'object' && current !== null && part in current) {
        current = current[part];
      } else {
        return defaultValue; // Path part not found
      }
    }

    return current;
  }

  /**
   * Get cart items
   *
   * @returns {Promise<Array>}
   */
  get items() {
    return fetch(this.BASE_URL.concat('/cart.js')).then(response => response.json()).then(data => data.items);
  }

  /**
   * Create hash
   *
   * @param {string} message
   * @param {string} algorithm
   * @returns {Promise<string>}
   */
  async digestMessage(message, algorithm="SHA-256") {
    const msgUint8 = new TextEncoder().encode(message);
    const hashBuffer = await window.crypto.subtle.digest(algorithm, msgUint8);
    return Array.from(new Uint8Array(hashBuffer))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("")
      .toUpperCase();
  }
}

new CartCrystalGenerate();
